import {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  Route,
  Routes,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router';
import i18n from 'i18next';

import {
  getStatus,
} from 'redux-resource';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Id,
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';
import {
  getFeatures,
} from 'web-selectors';

import {
  Button,
  Spinner,
  NotFound,
  AddEntity,
  NavigationTabs,
  ContentWrapper,
} from 'web-components';
import {
  Flex,
  SVG,
} from 'web-styled';

import Header from './Header';
import Members from './Members';
import Templates from './Templates';

type TabName = 'members' | 'programs' | 'workouts' | 'documents';
interface AddModal {
  type?: 'workout' | 'program';
  resourceType?: 'programTemplates' | 'workoutTemplates';
  isOpen: boolean;
}

const ClientGroupsPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tabName = location.pathname.slice(location.pathname.lastIndexOf('/') + 1) as TabName;
  const groupId = +params.id!;
  const [addItemModal, setAddItemModal] = useState<AddModal>({
    isOpen: false,
  });

  const mapState = useCallback(state => ({
    groupInfo: coreSelectors.getResourceById(
      'clientGroups',
      groupId,
    )(state),
    selectedIds: coreSelectors.getSelectedIds('clients')(state),
    readStatus: getStatus(
      state,
      [
        'clientGroups.requests.getGroupEntity.status',
      ],
      true,
    ),
    features: getFeatures(state),
  }), [groupId]);

  const {
    features,
    groupInfo,
    selectedIds,
    readStatus,
  } = useSelector(mapState, shallowEqual);

  const dispatch = useDispatch();

  const getTabs = () => {
    const tabs = [
      'members',
      'programs',
      'workouts',
    ]

    if (features.documents) {
      tabs.push('documents')
    }

    return tabs.map(
      name => ({
        name: i18n.t(name),
        to: `/client_groups/${groupId}/${name}`,
      }),
    );
  }


  const onRequestDeleteMembers = (selectedClientIds: Id[] = selectedIds) => {
    const onConfirmAction = coreActions.requestDeleteClientFromGroup({
      groupId: groupInfo.id,
      selectedClientIds,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Remove client(s) from group',
          bodyText: 'Are you sure you want to remove this client(s) from this group?',
          confirmText: 'Remove',
        },
        onConfirmAction,
      }),
    );
  };

  const handleAddItem = (values, actions) => {
    const onSuccess = (templateId, templateType, listName) => {
      dispatch(
        coreActions.requestAssignGroup({
          id: groupId,
          payload: {
            template_id: templateId,
            template_type: templateType,
          },
          listName,
        }),
      );
    };
    dispatch(
      coreActions.requestAddClientEntity({
        payload: {
          ...values,
          is_visible: false,
          is_warmup: false,
        },
        groupId: groupInfo.id,
        resourceType: addItemModal.resourceType,
        callbacks: {
          ...actions,
          onSuccess,
        },
      }),
    );
    setAddItemModal({ isOpen: false });
  };

  useEffect(() => {
    dispatch(
      coreActions.requestGroupEntity(groupId, 'clientGroups'),
    );
  }, [groupId]);

  return (
    <Flex
      fillSpace
      column
    >
      {readStatus.succeeded && (
        <>
          <Header
            groupInfo={groupInfo}
          />
          <Flex
            fillSpace
            margin="30px 0 0 0"
          >
            <ContentWrapper
              header={(
                <Flex
                  fillSpace
                  align="center"
                  justify="space-between"
                >
                  <Flex
                    align="center"
                    spacing={10}
                  >
                    <Button
                      icon={<SVG.ChevronLeft size={14} />}
                      appearance="link"
                      onClick={() => {
                        navigate('/clients/groups');
                      }}
                    />
                    <NavigationTabs
                      tabs={getTabs()}
                    />
                  </Flex>
                  <Flex
                    spacing={10}
                  >
                    {tabName === 'programs' && (
                      <Button
                        ghost
                        icon={<SVG.Plus size={14} strokeWidth={4} />}
                        appearance="primary"
                        onClick={() => {
                          setAddItemModal({
                            isOpen: true,
                            type: 'program',
                            resourceType: 'programTemplates',
                          });
                        }}
                      >
                        {i18n.t('add.program')}
                      </Button>
                    )}
                    {tabName === 'workouts' && (
                      <Button
                        ghost
                        icon={<SVG.Plus size={14} strokeWidth={4} />}
                        appearance="primary"
                        onClick={() => {
                          setAddItemModal({
                            isOpen: true,
                            type: 'workout',
                            resourceType: 'workoutTemplates',
                          });
                        }}
                      >
                        {i18n.t('add.workout')}
                      </Button>
                    )}
                    {(tabName === 'members' && selectedIds.length > 0) && (
                      <Button
                        ghost
                        appearance="danger"
                        onClick={() => onRequestDeleteMembers()}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Flex>
              )}
            >
              <Routes>
                <Route
                  index
                  element={(
                    <Navigate to="members" replace />
                  )}
                />
                <Route
                  path="members"
                  element={(
                    <Members
                      groupId={groupId}
                      resourceType="clients"
                      selectedIds={selectedIds}
                      onRemove={(id) => onRequestDeleteMembers([id])}
                    />
                  )}
                />
                <Route
                  path="programs"
                  element={(
                    <Templates
                      type="program"
                      groupId={groupId}
                      resourceType="programTemplates"
                    />
                  )}
                />
                <Route
                  path="workouts"
                  element={(
                    <Templates
                      type="workout"
                      groupId={groupId}
                      resourceType="workoutTemplates"
                    />
                  )}
                />
                {features.documents && (
                  <Route
                    path="documents"
                    element={(
                      <Templates
                        type="document"
                        groupId={groupId}
                        resourceType="documents"
                      />
                    )}
                  />
                )}
              </Routes>
            </ContentWrapper>

            <AddEntity
              label={`Create new ${addItemModal.type} for ${groupInfo.name}`}
              buttonLabel="Create"
              closeButton
              resourceType={addItemModal.resourceType}
              appearance="danger"
              isOpen={addItemModal.isOpen}
              onRequestClose={() => {
                setAddItemModal({ isOpen: false });
              }}
              onSubmit={handleAddItem}
            />
          </Flex>
        </>
      )}

      {readStatus.pending && (
        <Spinner message="Loading group details..." />
      )}

      {readStatus.failed && (
        <NotFound title="Group Not Found..." />
      )}

    </Flex>
  );
};

export default ClientGroupsPage;
